// License: MIT
// Author: Anton Medvedev <anton@medv.io>
// Source: https://github.com/antonmedv/finder
// Version: 3.2.0 (29 Feb 2024)

type Knot = {
  name: string;
  penalty: number;
  level?: number;
};

type Path = Knot[];

export type Options = {
  root: Element;
  idName: (name: string) => boolean;
  className: (name: string) => boolean;
  tagName: (name: string) => boolean;
  attr: (name: string, value: string) => boolean;
  seedMinLength: number;
  optimizedMinLength: number;
  threshold: number;
  maxNumberOfTries: number;
  timeoutMs: number | undefined;
};

let config: Options;
let rootDocument: Document | Element;
let start: Date;

export function finder(input: Element, document = window.document, options?: Partial<Options>) {
  start = new Date();
  if (input.nodeType !== Node.ELEMENT_NODE) {
    // if node is not an Element node then ignore and return an undefined selector
    // but don't throw error like in original code
    return undefined;
  }
  if (input.tagName.toLowerCase() === 'html') {
    return 'html';
  }
  const defaults: Options = {
    root: document.body,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    idName: (name: string) => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    className: (name: string) => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tagName: (name: string) => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    attr: (name: string, value: string) => false,
    seedMinLength: 1,
    optimizedMinLength: 2,
    threshold: 1000,
    maxNumberOfTries: 200,
    timeoutMs: 20,
  };

  config = { ...defaults, ...options };
  rootDocument = document;

  const path = bottomUpSearch(input, 'all', () =>
    bottomUpSearch(input, 'two', () =>
      bottomUpSearch(input, 'one', () => bottomUpSearch(input, 'none')),
    ),
  );

  if (path) {
    const optimized = sort(optimize(path, input));
    return selector(optimized.length > 0 ? optimized[0] : path);
  }

  // if no selector is found then return undefined instead of throwing error like in original code
  return undefined;
}

function bottomUpSearch(
  input: Element,
  limit: 'all' | 'two' | 'one' | 'none',
  fallback?: () => Path | null,
): Path | null {
  let path: Path | null = null;
  const stack: Knot[][] = [];
  let current: Element | null = input;
  let i = 0;
  while (current) {
    const elapsedTime = new Date().getTime() - start.getTime();
    if (config.timeoutMs !== undefined && elapsedTime > config.timeoutMs) {
      throw new Error(`Timeout: Can't find a unique selector after ${elapsedTime}ms`);
    }
    let level: Knot[] = maybe(id(current)) ||
      maybe(...attr(current)) ||
      maybe(...classNames(current)) ||
      maybe(tagName(current)) || [any()];
    const nthType = typeIndex(current);
    if (limit === 'all') {
      if (nthType) {
        level = level.concat(level.filter(dispensableNth).map((node) => nthOfType(node, nthType)));
      }
    } else if (limit === 'two') {
      level = level.slice(0, 1);
      if (nthType) {
        level = level.concat(level.filter(dispensableNth).map((node) => nthOfType(node, nthType)));
      }
    } else if (limit === 'one') {
      level = level.slice(0, 1);
      const [node] = level;
      if (nthType && dispensableNth(node)) {
        level = [nthOfType(node, nthType)];
      }
    } else if (limit === 'none') {
      level = [any()];
      if (nthType) {
        level = [nthOfType(level[0], nthType)];
      }
    }

    const currentLevelIndex = i;
    stack.push(level.map((node) => ({ ...node, level: currentLevelIndex })));

    if (stack.length >= config.seedMinLength) {
      path = findUniquePath(stack, fallback);
      if (path) {
        break;
      }
    }
    current = current.parentElement;
    i += 1;
  }
  if (!path) {
    path = findUniquePath(stack, fallback);
  }
  if (!path && fallback) {
    return fallback();
  }
  return path;
}

function findUniquePath(stack: Knot[][], fallback?: () => Path | null): Path | null {
  const paths = sort(combinations(stack));
  if (paths.length > config.threshold) {
    return fallback ? fallback() : null;
  }

  const uniquePath = paths.find((candidate) => unique(candidate));

  if (typeof uniquePath !== 'undefined') {
    return uniquePath;
  }

  return null;
}

function selector(path: Path): string {
  let node = path[0];
  let query = node.name;
  for (let i = 1; i < path.length; i += 1) {
    const level = path[i].level || 0;
    if (node.level === level - 1) {
      query = `${path[i].name} > ${query}`;
    } else {
      query = `${path[i].name} ${query}`;
    }
    node = path[i];
  }
  return query;
}

function penalty(path: Path): number {
  return path.map((node) => node.penalty).reduce((acc, i) => acc + i, 0);
}

function unique(path: Path) {
  const css = selector(path);
  switch (rootDocument.querySelectorAll(css).length) {
    case 0:
      throw new Error(`Can't select any node with this selector: ${css}`);
    case 1:
      return true;
    default:
      return false;
  }
}

function id(input: Element): Knot | null {
  const elementId = input.getAttribute('id');
  if (elementId && config.idName(elementId)) {
    return {
      name: `#${CSS.escape(elementId)}`,
      penalty: 0,
    };
  }
  return null;
}

function attr(input: Element): Knot[] {
  const attrs = Array.from(input.attributes).filter((entry) =>
    config.attr(entry.name, entry.value),
  );
  return attrs.map(
    (entry): Knot => ({
      name: `[${CSS.escape(entry.name)}="${CSS.escape(entry.value)}"]`,
      penalty: 0.5,
    }),
  );
}

function classNames(input: Element): Knot[] {
  const names = Array.from(input.classList).filter(config.className);
  return names.map(
    (entry): Knot => ({
      name: `.${CSS.escape(entry)}`,
      penalty: 1,
    }),
  );
}

function tagName(input: Element): Knot | null {
  const name = input.tagName.toLowerCase();
  if (config.tagName(name)) {
    return {
      name,
      penalty: 2,
    };
  }
  return null;
}

function any(): Knot {
  return {
    name: '*',
    penalty: 3,
  };
}

function typeIndex(input: Element): number | null {
  const parent = input.parentNode;
  if (!parent) {
    return null;
  }
  let child = parent.firstChild;
  if (!child) {
    return null;
  }
  let i = 0;
  while (child) {
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === input.tagName) {
      i += 1;
    }
    if (child === input) {
      break;
    }
    child = child.nextSibling;
  }
  return i;
}

function nthOfType(node: Knot, typeIdx: number): Knot {
  return {
    name: `${node.name}:nth-of-type(${typeIdx})`,
    penalty: node.penalty + 1,
  };
}

function dispensableNth(node: Knot) {
  return node.name !== 'html' && !node.name.startsWith('#');
}

function maybe(...level: (Knot | null)[]): Knot[] | null {
  const list = level.filter(notEmpty);
  if (list.length > 0) {
    return list;
  }
  return null;
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function* combinations(stack: Knot[][], path: Knot[] = []): Generator<Knot[]> {
  if (stack.length > 0) {
    // yield needs to be in a for-of loop as it will not work with an array iterator
    // eslint-disable-next-line no-restricted-syntax
    for (const node of stack[0]) {
      yield* combinations(stack.slice(1, stack.length), path.concat(node));
    }
  } else {
    yield path;
  }
}

function sort(paths: Iterable<Path>): Path[] {
  return [...paths].sort((a, b) => penalty(a) - penalty(b));
}

type Scope = {
  counter: number;
  visited: Map<string, boolean>;
};

function* optimize(
  path: Path,
  input: Element,
  scope: Scope = {
    counter: 0,
    visited: new Map<string, boolean>(),
  },
): Generator<Knot[]> {
  if (path.length > 2 && path.length > config.optimizedMinLength) {
    const iterationScope = { ...scope };
    for (let i = 1; i < path.length - 1; i += 1) {
      if (iterationScope.counter > config.maxNumberOfTries) {
        return; // Okay At least I tried!
      }
      iterationScope.counter += 1;
      const newPath = [...path];
      newPath.splice(i, 1);
      const newPathKey = selector(newPath);
      if (iterationScope.visited.has(newPathKey)) {
        return;
      }
      if (unique(newPath) && same(newPath, input)) {
        yield newPath;
        iterationScope.visited.set(newPathKey, true);
        yield* optimize(newPath, input, iterationScope);
      }
    }
  }
}

function same(path: Path, input: Element) {
  return rootDocument.querySelector(selector(path)) === input;
}
